import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {RegExp12DigitInteger2DigitFraction} from "@app/shared/classes/reg-exp";
import {Format} from "@app/shared/enums/format.enum";
import {Validation} from "@app/shared/enums/validation.enum";
import {CompletionEstimate} from "@app/shared/completion-estimate/completion-estimate";
import {CompletionEstimateService} from "@app/shared/completion-estimate/completion-estimate.service";
import {classToClass, plainToClass} from "class-transformer";
import {CalendarService} from "@app/shared/services/calendar.service";
import {Calendar} from "@app/shared/calendar/calendar";
import {ProjectCostInfo} from "@app/shared/classes/project-cost-info";
import {CalendarType} from "@app/shared/enums/calendar-type.enum";
import {PrimeNgConfig, PrimeNgSupportService, TableColumn, TableFormat} from "@app/shared";

@Component({
  selector: 'app-completion-estimate',
  templateUrl: './completion-estimate.component.html',
  styleUrls: ['./completion-estimate.component.css'],
  providers: [CompletionEstimateService, CalendarService]

})
export class CompletionEstimateComponent implements OnInit, OnChanges {

  @Input() contractId: number;
  @Input() clinId: number;
  @Input() projectId: number;
  @Input() projectCostInfo: ProjectCostInfo[];

  Format: typeof Format = Format;
  PrimeNgConfig = PrimeNgConfig;
  RegExp12DigitInteger2DigitFraction = RegExp12DigitInteger2DigitFraction;
  Validation: typeof Validation = Validation;

  estimatedCompletions: CompletionEstimate[];
  latestEstimatedCompletion: CompletionEstimate;
  invoicingPeriod: Calendar;
  estimatedCompletionsTableColumns: TableColumn[];
  projectItdTableColumns: TableColumn[];


  constructor(private dataService: CompletionEstimateService, private calendarService: CalendarService, private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.initTableColumns();
    this.calendarService.getCurrentCalendar(CalendarType.Invoicing).subscribe(res => {
      this.invoicingPeriod = plainToClass(Calendar, res);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataService.searchEstimatedCompletions(this.contractId, this.clinId, this.projectId).subscribe(res => {
      this.estimatedCompletions = plainToClass(CompletionEstimate, res.list).sort((a, b) => a.id < b.id ? 1 : -1);
      this.setLatestEstimatedCompletion(this.estimatedCompletions);
    });
  }

  onClickUpdateEstimatedCompletion(estimateCompletion: CompletionEstimate, incurredCost: number, invoicingPeriod: Calendar): void {
    estimateCompletion.incurredCost = incurredCost;

    // if invoicing period is different, create a new CompletionEstimate record
    if (invoicingPeriod.id != estimateCompletion.invoicingPeriodId) {
      estimateCompletion.id = null;
      estimateCompletion.version = null;
      estimateCompletion.invoicingPeriodId = invoicingPeriod.id;
    }

    this.dataService.saveOrUpdateEstimatedCompletion(estimateCompletion).subscribe(res => {
      const savedEstimatedCompletion = plainToClass(CompletionEstimate, res);
      const index = this.estimatedCompletions.findIndex(ec => ec.id == savedEstimatedCompletion.id);
      if (index >= 0) {
        this.estimatedCompletions = this.primeNgService.replaceByIndex(this.estimatedCompletions, savedEstimatedCompletion, index);
      } else {
        this.estimatedCompletions = this.primeNgService.unshift(this.estimatedCompletions, savedEstimatedCompletion);
        this.estimatedCompletions.unshift(savedEstimatedCompletion);
      }
      this.latestEstimatedCompletion = classToClass(savedEstimatedCompletion);
    });
  }

  private initTableColumns(): void {
    this.estimatedCompletionsTableColumns = [
      {field: 'invoicePeriodTitle', header: 'Invoice Period'},
      {field: 'updatedOn', header: 'Updated On', format: TableFormat.Date},
      {field: 'updatedByName', header: 'Updated By'},
      {field: 'estimatedCost', header: 'Estimated Cost', format: TableFormat.Currency},
      {field: 'incurredCost', header: 'Incurred Cost', format: TableFormat.Currency},
    ];

    this.projectItdTableColumns = [
      {field: 'employeeLaborITD', header: 'Employee Labor', format: TableFormat.Currency},
      {field: 'subcontractorLaborITD', header: 'Subcontractor Labor', format: TableFormat.Currency},
      {field: 'travelITD', header: 'Travel', format: TableFormat.Currency},
      {field: 'trainingITD', header: 'Training', format: TableFormat.Currency},
      {field: 'materialITD', header: 'Material', format: TableFormat.Currency},
      {field: 'odcITD', header: 'ODC', format: TableFormat.Currency},
      {field: 'totalCostITD', header: 'Total', format: TableFormat.Currency},
    ];
  }

  private setLatestEstimatedCompletion(estimatedCompletions: CompletionEstimate[]): void {
    if (estimatedCompletions.length > 0) {
      this.latestEstimatedCompletion = classToClass(estimatedCompletions[0]);
    } else {
      this.latestEstimatedCompletion = new CompletionEstimate();
      this.latestEstimatedCompletion.clinId = this.clinId;
      this.latestEstimatedCompletion.projectId = this.projectId;
      this.latestEstimatedCompletion.contractId = this.contractId;
    }
  }
}
